import { Popover } from "radix-ui";
import { SideBarMenuButton } from "../../../../ProjectFilter/SideBarMenu/SideBarMenuButton";
import { ScrollArea } from "../../../../atom/ScrollArea";
import { useCallback, useEffect, useState, useRef } from "react";
import DoneIcon from "../../../../atom/icons/DoneIcon";
import InboxBlockBody from "../../../../TaskInbox/InboxBody/InboxBlock/InboxBlockBody";
import { useInboxCompletedQuery } from "../../../../../react-query/inbox/queries";
import clsx from "clsx";
import useHandleUpdateInboxCheckbox from "../../../../../hooks/inbox/useHandleUpdateInboxCheckbox";
import { BlockType } from "../../../../../types/block/type";
import { useUpdateInboxCheckboxMutation } from "../../../../../react-query/inbox/useUpdateInboxCheckboxMutation";
import { useRecoilState } from "recoil";
import { taskPopupState } from "../../../../../recoil/taskDetail/taskPopupState";
import useHandleJuneBlockData from "../../../../../hooks/june/useHandleJuneBlockdata";
import { BasicBlock } from "../../../../../types/block/enum";
import ModalPanel from "../Common";
import { juneTrack } from "../../../../../utils/june/analytics";
import { JUNE_EVENT } from "../../../../../hooks/june/juneEvent";
import { useVirtualizer } from "@tanstack/react-virtual";

const CompletedTasksPopover = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [taskSelectedId, setTaskSelectedId] = useState<number | null>(null);

  const [taskDetail, setTaskDetail] = useRecoilState(taskPopupState);

  const { data: completedTasks } = useInboxCompletedQuery();
  const useUpdateInboxCheckbox = useUpdateInboxCheckboxMutation();

  const { handleClickCheckBox } = useHandleUpdateInboxCheckbox();
  const handleJuneBlockData = useHandleJuneBlockData();

  // 가상화를 위한 ref
  const parentRef = useRef<HTMLDivElement>(null);

  // 가상화 설정
  const virtualizer = useVirtualizer({
    count: completedTasks?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 34,
    overscan: 10,
    gap: 8,
    enabled: isOpen,
    getItemKey: useCallback(
      (index: number) => completedTasks?.[index]?.id ?? index,
      [completedTasks]
    ),
  });

  // 팝업이 열리고, DOM이 준비되면 virtualizer 재측정
  useEffect(() => {
    if (isOpen) {
      virtualizer.measure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!taskDetail.data?.id) {
      setTaskSelectedId(null);
    }
  }, [taskDetail.data?.id]);

  const handleOpenChange = (open: boolean) => {
    if (taskDetail.data) return;
    setIsOpen(open);
    open && juneTrack(JUNE_EVENT.VIEW_COMPLETED_TASK);
  };

  const handleClickInboxBlock = useCallback(
    (e: React.MouseEvent, data: BlockType) => {
      if (taskSelectedId === data.id) {
        return setTaskSelectedId(null);
      }

      setTaskSelectedId(data.id);

      const { clientX, clientY } = e;
      const rect = e.currentTarget?.getBoundingClientRect();

      if (clientX !== 0 || clientY !== 0) {
        setTaskDetail({
          isVisible: true,
          data: data,

          targetRect: rect,
          rightOffset: 13,
          onClose: () => {},
          type: "inbox",
        });
      }

      handleJuneBlockData({
        action: "view_block_detail",
        location: "inbox",
        blockType: BasicBlock.TASK,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskSelectedId]
  );

  const onClickCheckBox = useCallback((e: React.MouseEvent, data: BlockType) => {
    e.stopPropagation();
    handleClickCheckBox(data, useUpdateInboxCheckbox, "completed_tasks");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Popover.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <Popover.Trigger asChild>
          <SideBarMenuButton
            className={isOpen ? "bg-w-8-hover" : ""}
            tooltipLabel={"Completed tasks"}
          >
            <DoneIcon color={isOpen ? "#69E4FF" : "#CACDCE"} />
          </SideBarMenuButton>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="end"
            alignOffset={-9}
            side="right"
            sideOffset={49}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()} // 외부 클릭 시 닫기 수동 제어(taskDetail과 같이 제어가 되야해서 따로 처리)
            className={clsx(
              "h-[520px] w-[300px] rounded-[7px] border border-solid border-25 bg-20 focus:outline-none",
              "origin-bottom-left data-[state=closed]:animate-popup-scale-out data-[state=open]:animate-popup-scale-in"
            )}
          >
            <ModalPanel.Header>
              <ModalPanel.Title>Completed tasks</ModalPanel.Title>
            </ModalPanel.Header>
            {completedTasks && completedTasks.length > 0 && (
              <ScrollArea
                ref={parentRef}
                height={470}
                showScrollBar={true}
                className="py-[4px] pl-[8px]"
              >
                <ul style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const task = completedTasks[virtualItem.index];
                    if (!task) return null;

                    return (
                      <li
                        key={virtualItem.key}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                      >
                        <div
                          className={clsx(
                            "flex w-full cursor-pointer flex-col gap-[8px] rounded-[5px] pl-[12px] hover:bg-w-8-hover",
                            taskSelectedId === task.id && "bg-w-8-hover"
                          )}
                          onClick={(e) => handleClickInboxBlock(e, task)}
                        >
                          <InboxBlockBody
                            data={task}
                            isOverdue={false}
                            isCompleted={true}
                            onClickCheckBox={onClickCheckBox}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* 외부 클릭 시 닫기 수동 제어(taskDetail과 같이 제어가 되야해서 따로 처리) */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[10]"
          onClick={() => {
            if (taskSelectedId) return;
            setIsOpen(false);
          }}
        ></div>
      )}
    </>
  );
};

export default CompletedTasksPopover;
